/**
 * Les repères d'un fil — ce qui n'est pas un message mais se lit entre eux :
 *
 *   · `SeparateurDeJour` : « Aujourd'hui », « Hier », « Dimanche 25 octobre », un `Badge` au centre
 *     sur un fond flouté ; `collant`, il reste en haut pendant qu'on remonte le jour ;
 *   · `SeparateurNonLus` : « Nouveaux messages », le trait d'où reprendre la lecture ;
 *   · `Evenement` : un message système (« Paiement reçu · 25 € », « Cyrano en pause »,
 *     « Le fan a démarré le bot »), centré, gris ; son ton (vert, ambre, rouge) ne colore que
 *     son point ou son icône ;
 *   · `Ecrit` : « Max écrit… » — trois points qui sautillent (immobiles sous « mouvement
 *     réduit »), en bulle dans le fil ou en bande au-dessus du compositeur.
 *
 * Les libellés arrivent déjà écrits et traduits ; le jour se formate dans l'app.
 */
import type { ReactNode } from "react";

import { Badge } from "../Badge";
import { cn } from "../utils";
import "./conversation.css";

export interface SeparateurDeJourProps {
  /** « Aujourd'hui », « Hier », « Dimanche 25 octobre ». */
  libelle: ReactNode;
  /** La date, pour `<time dateTime>` (« 2026-10-25 »). */
  dateTime?: string;
  /** Reste en haut du fil pendant qu'on remonte ce jour. */
  collant?: boolean;
  className?: string;
}

export function SeparateurDeJour({ libelle, dateTime, collant = false, className }: SeparateurDeJourProps) {
  return (
    <div className={cn("cv-jour", className)} data-collant={collant || undefined}>
      <span className="cv-jour-fond">
        <Badge ton="gris" size="md">
          <time dateTime={dateTime}>{libelle}</time>
        </Badge>
      </span>
    </div>
  );
}

export interface SeparateurNonLusProps {
  /** Défaut « Nouveaux messages ». */
  libelle?: ReactNode;
  className?: string;
}

export function SeparateurNonLus({ libelle = "Nouveaux messages", className }: SeparateurNonLusProps) {
  return (
    <div className={cn("cv-nonlus", className)} role="separator" aria-label={typeof libelle === "string" ? libelle : undefined}>
      {libelle}
    </div>
  );
}

export type TonEvenement = "neutre" | "vert" | "ambre" | "rouge";

export interface EvenementProps {
  children: ReactNode;
  /** Une icône (de `@dozka/ui/icons`) à la place du point. */
  icone?: ReactNode;
  /** Le sens : vert payé ou validé, ambre attention, rouge erreur. Défaut `neutre`. */
  ton?: TonEvenement;
  /** Montre le point de couleur quand il n'y a pas d'icône. Défaut : oui si le ton n'est pas neutre. */
  point?: boolean;
  /** L'heure, en petit, à droite (« 14:32 »). */
  heure?: string;
  className?: string;
}

export function Evenement({ children, icone, ton = "neutre", point, heure, className }: EvenementProps) {
  const avecPoint = icone === undefined && (point ?? ton !== "neutre");
  return (
    <div className="flex justify-center">
      <div className={cn("cv-evenement", className)} data-ton={ton === "neutre" ? undefined : ton}>
        {icone}
        {avecPoint && <span className="cv-evenement-point" aria-hidden />}
        <span className="min-w-0">{children}</span>
        {heure && <span className="cv-evenement-heure">{heure}</span>}
      </div>
    </div>
  );
}

export interface EcritProps {
  /** « Max écrit… » — déjà traduit. Absent : les points seuls (le lecteur d'écran entend `ariaLabel`). */
  libelle?: ReactNode;
  /** `bulle` : dans le fil, comme un message qui arrive. `bande` : au-dessus du compositeur. Défaut `bulle`. */
  variante?: "bulle" | "bande";
  /** Pour le lecteur d'écran quand `libelle` n'est pas un texte. Défaut « écrit… ». */
  ariaLabel?: string;
  className?: string;
}

export function Ecrit({ libelle, variante = "bulle", ariaLabel = "écrit…", className }: EcritProps) {
  return (
    <div
      className={cn("cv-ecrit", className)}
      data-variante={variante}
      role="status"
      aria-live="polite"
      aria-label={typeof libelle === "string" ? libelle : ariaLabel}
    >
      <span className="cv-ecrit-bulle" aria-hidden>
        <span className="cv-ecrit-point" />
        <span className="cv-ecrit-point" />
        <span className="cv-ecrit-point" />
      </span>
      {libelle !== undefined && libelle !== null && <span aria-hidden>{libelle}</span>}
    </div>
  );
}
